import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Spin,
  Typography,
  Button,
  Modal,
  Input,
  message,
} from 'antd';
import {
  EditOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import EmployeeFundService from '../../../service/employee-service/Employee-fund.service';
import { FundTransferResponse } from '../../../service/employee-service/Employee-fund.service';

const { Title } = Typography;
const { confirm } = Modal;

interface TransferHistoryProps {
  employeeId: number;
}

const TransferHistory: React.FC<TransferHistoryProps> = ({ employeeId }) => {
  const [transfers, setTransfers] = useState<FundTransferResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTransfer, setEditingTransfer] =
    useState<FundTransferResponse | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [newAmount, setNewAmount] = useState<string>('');
  const [newDescription, setNewDescription] = useState<string>('');

  const fetchTransfers = async () => {
    try {
      setLoading(true);
      const data =
        await EmployeeFundService.getTransfersByEmployeeId(employeeId);
      setTransfers(data);
    } catch (error) {
      console.error('Error fetching transfers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransfers();
  }, [employeeId]);

  const handleEditTransfer = (transfer: FundTransferResponse) => {
    setEditingTransfer(transfer);
    setNewAmount(transfer.amount.toString());
    setNewDescription(transfer.description || '');
    setEditModalVisible(true);
  };

  const handleCancelTransfer = (transferId: number) => {
    confirm({
      title: 'Xác nhận hủy chuyển tiền',
      icon: <ExclamationCircleOutlined />,
      content: 'Bạn có chắc chắn muốn hủy giao dịch chuyển tiền này?',
      okText: 'Xác nhận',
      cancelText: 'Hủy',
      okType: 'danger',
      onOk: async () => {
        try {
          await EmployeeFundService.cancelFundTransfer(transferId);
          message.success('Đã hủy giao dịch chuyển tiền thành công');
          fetchTransfers();
        } catch (error) {
          console.error('Error canceling transfer:', error);
          message.error('Có lỗi xảy ra khi hủy giao dịch');
        }
      },
    });
  };

  const handleSaveEdit = async () => {
    if (!editingTransfer || !newAmount || Number(newAmount) <= 0) {
      message.error('Vui lòng nhập số tiền hợp lệ');
      return;
    }

    try {
      await EmployeeFundService.editFundTransfer(
        editingTransfer.id,
        Number(newAmount),
        newDescription
      );
      message.success('Đã cập nhật thông tin chuyển tiền thành công');
      setEditModalVisible(false);
      setEditingTransfer(null);
      fetchTransfers();
    } catch (error) {
      console.error('Error editing transfer:', error);
      message.error('Có lỗi xảy ra khi cập nhật thông tin');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'green';
      case 'CANCELLED':
        return 'red';
      case 'PENDING':
        return 'orange';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'Hoàn thành';
      case 'CANCELLED':
        return 'Đã hủy';
      case 'PENDING':
        return 'Đang xử lý';
      default:
        return status;
    }
  };

  const columns = [
    {
      title: 'Ngày chuyển',
      dataIndex: 'transferDate',
      key: 'transferDate',
      render: (date: string) => {
        return new Date(date).toLocaleString('vi-VN');
      },
      sorter: (a: FundTransferResponse, b: FundTransferResponse) =>
        new Date(a.transferDate).getTime() - new Date(b.transferDate).getTime(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => amount.toLocaleString('vi-VN') + ' VND',
      sorter: (a: FundTransferResponse, b: FundTransferResponse) =>
        a.amount - b.amount,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusLabel(status)}</Tag>
      ),
    },

    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => (
        <span className="text-sm text-gray-600" title={description}>
          {description && description.length > 30
            ? description.substring(0, 30) + '...'
            : description}
        </span>
      ),
    },
  ];

  return (
    <>
      <Card
        title={
          <div className="flex items-center gap-2">
            <Title level={4} style={{ margin: 0 }}>
              Lịch sử chuyển tiền về công ty
            </Title>
          </div>
        }
        bordered
        className="shadow-sm hover:shadow-md transition-shadow duration-300"
      >
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={transfers}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} của ${total} bản ghi`,
            }}
            scroll={{ x: 1200 }}
          />
        )}
      </Card>

      {/* Modal chỉnh sửa chuyển tiền */}
      <Modal
        title="Chỉnh sửa thông tin chuyển tiền"
        open={editModalVisible}
        onOk={handleSaveEdit}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingTransfer(null);
        }}
        okText="Lưu"
        cancelText="Hủy"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Số tiền mới:
            </label>
            <Input
              value={newAmount}
              onChange={(e) => setNewAmount(e.target.value.replace(/\D/g, ''))}
              placeholder="Nhập số tiền"
              suffix="VND"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Mô tả mới:</label>
            <Input.TextArea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Nhập mô tả"
              rows={3}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TransferHistory;
